import React, { useEffect, useMemo, useRef } from "react";
import ReactFlow, {
  Background,
  ReactFlowProvider,
  getSmoothStepPath,
  BaseEdge,
  EdgeLabelRenderer,
} from "reactflow";
import "reactflow/dist/style.css";
import {
  buildFlowElements,
  columnX,
  DEFAULT_DESTINATIONS_PER_PAGE,
  STANDARD_A4_FRAME,
} from "./diagramUtils";
import repartisLogo from "../../assets/images/logo.svg";
// M1.1b: PP Neue Montreal is referenced via inline fontFamily below but the
// @font-face declarations only existed under the Phase 3 Répartition flow.
// Importing the shared CSS here makes the same font available to the Key
// Diagram Report so both PDFs render in matching typography (per client
// feedback "model the most similar on Key graphs report"). The CSS file lives
// under RepartitionReport/ for historical reasons but its @font-face is
// page-agnostic — only the .repartition-page class rule is Phase 3 specific,
// and that class is not used here.
import "../RepartitionReport/repartition-fonts.css";

// Custom edge component for transfer labels (right-angle)
const TransferEdge = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, label, labelStyle }) => {
  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: 12,
  });

  const offsetX = -40;
  const offsetY = -4;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          stroke: "#b1b1b7",
          strokeWidth: 2,
        }}
      />
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: "absolute",
              transform: `translate(${targetX + offsetX}px, ${targetY + offsetY}px)`,
              fontSize: labelStyle?.fontSize || 20,
              fontWeight: labelStyle?.fontWeight || 600,
              pointerEvents: "none",
              zIndex: 1000,
              color: "#00033F",
            }}
            className="edge-label-html nodrag nopan"
          >
            <span
              style={{
                background: "transparent",
                whiteSpace: "nowrap",
                fontFamily: '"PP Neue Montreal", sans-serif',
              }}
            >
              {label}
            </span>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

// Custom edge component with HTML label
const LabeledEdge = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, label, labelStyle, data }) => {
  // Calculate distance to determine routing
  const horizontalDistance = Math.abs(targetX - sourceX);
  
  // Determine if target is in Column 4 (destination2) - route between Column 3 and Column 4
  // Column 3 (buyer1) is at 780, Column 4 (destination2) is at 1030
  // Check column data - Column 4 destinations have column='right'
  const isColumn4 = data?.column === 'right';
  
  let edgePath;
  if (isColumn4) {
    // For Column 4 destinations, route through gap between Column 3 and Column 4 ONLY
    // Column 3 (buyer1) is at 780, Column 4 (destination2) is at 1030
    // The path must turn vertically BETWEEN Column 3 and Column 4, not between Column 2 and Column 3
    // Position waypoint to ensure the vertical turn happens in the gap between Column 3 and Column 4
    // Route in the true gap between buyer1 right edge and destination2 left edge
    const buyerRightEdge = columnX.buyer1 + 160;
    const destination2LeftEdge = columnX.destination2;
    const waypointX = (buyerRightEdge + destination2LeftEdge) / 2;
    const borderRadius = 15;
    const isGoingDown = targetY > sourceY;
    
    // Create path that routes through the gap between Column 3 and Column 4 ONLY
    // Path structure: horizontal from source -> vertical turn at waypoint (between Column 3 and 4) -> horizontal to target
    // The waypoint at 967 ensures the vertical turn (and branching) happens clearly between Column 3 (780) and Column 4 (1030)
    // This prevents the lines from branching between Column 2 and Column 3
    if (isGoingDown) {
      edgePath = `M ${sourceX},${sourceY} ` +
                 `H ${waypointX - borderRadius} ` +
                 `Q ${waypointX},${sourceY} ${waypointX},${sourceY + borderRadius} ` +
                 `V ${targetY - borderRadius} ` +
                 `Q ${waypointX},${targetY} ${waypointX + borderRadius},${targetY} ` +
                 `H ${targetX}`;
    } else {
      edgePath = `M ${sourceX},${sourceY} ` +
                 `H ${waypointX - borderRadius} ` +
                 `Q ${waypointX},${sourceY} ${waypointX},${sourceY - borderRadius} ` +
                 `V ${targetY + borderRadius} ` +
                 `Q ${waypointX},${targetY} ${waypointX + borderRadius},${targetY} ` +
                 `H ${targetX}`;
    }
  } else {
    // For other destinations, use standard routing
    const borderRadius = horizontalDistance > 500 ? 25 : 12;
    [edgePath] = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
      borderRadius: borderRadius,
    });
  }

  // Position label near the target (surface/consumption) box
  // Calculate position closer to target node
  const offsetX = -40; // Offset to the left of target
  const offsetY = -4; // Vertical offset with 2px spacing from the line

  // Determine edge color based on target column for visual distinction
  // Column 2 (left) = blue, Column 4 (right) = red
  const edgeColor = isColumn4 ? "#ff4444" : "#3b82f6"; // Red for Column 4, Blue for Column 2

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          stroke: edgeColor,
          strokeWidth: 2,
          strokeOpacity: 1,
          visibility: "visible",
          display: "block",
          fill: "none",
        }}
      />
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: "absolute",
              transform: `translate(${targetX + offsetX}px, ${targetY + offsetY}px)`,
              fontSize: labelStyle?.fontSize || 20,
              fontWeight: labelStyle?.fontWeight || 600,
              pointerEvents: "none",
              zIndex: 1000,
              color: "#00033F",
            }}
            className="edge-label-html nodrag nopan"
          >
            <span
              style={{
                background: "transparent",
                whiteSpace: "nowrap",
                fontFamily: '"PP Neue Montreal", sans-serif',
              }}
            >
              {label}
            </span>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

// Custom edge component for straight lines between destinations and buyers/targets
const StraightEdge = ({ id, sourceX, sourceY, targetX, targetY }) => {
  const midY = (sourceY + targetY) / 2;
  const edgePath = `M ${sourceX},${midY} L ${targetX},${midY}`;
  
  return (
    <BaseEdge
      id={id}
      path={edgePath}
      style={{
        stroke: "#b1b1b7",
        strokeWidth: 2,
      }}
    />
  );
};

const edgeTypes = {
  transfer: TransferEdge,
  labeled: LabeledEdge,
  straight: StraightEdge,
};

const formatPeriodRange = (keyData) => {
  if (!keyData) return "-";
  const { start_date: startDate, end_date: endDate } = keyData;
  if (!startDate && !endDate) return "-";
  try {
    const format = (value) =>
      value
        ? new Date(value).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
        : "-";
    return `${format(startDate)} - ${format(endDate)}`;
  } catch {
    return "-";
  }
};

// This component renders a single Key Diagram (without export functionality)
// It's a simplified version that renders the diagram structure
const KeyDiagramView = ({ keyId, reportData, onDiagramReady, whiteLabellingEnabled, customLogoUrl }) => {
  const pageFrameRefs = useRef([]);

  // Suppress benign ResizeObserver loop error that Chrome sometimes emits
  useEffect(() => {
    const handler = (e) => {
      const msg = (e?.message || e?.reason?.message || '').toString();
      if (msg.includes('ResizeObserver loop completed with undelivered notifications')) {
        e.preventDefault?.();
        e.stopPropagation?.();
        e.stopImmediatePropagation?.();
      }
    };
    window.addEventListener('error', handler, true);
    window.addEventListener('unhandledrejection', handler, true);
    return () => {
      window.removeEventListener('error', handler, true);
      window.removeEventListener('unhandledrejection', handler, true);
    };
  }, []);

  const firstPageLayout = useMemo(
    () => buildFlowElements(reportData, null, { page: 0, destinationsPerPage: DEFAULT_DESTINATIONS_PER_PAGE }),
    [reportData]
  );
  const totalPages = firstPageLayout.totalPages || 1;
  const pageLayouts = useMemo(
    () =>
      Array.from({ length: totalPages }, (_, pageIndex) =>
        buildFlowElements(reportData, null, {
          page: pageIndex,
          destinationsPerPage: DEFAULT_DESTINATIONS_PER_PAGE,
        })
      ),
    [reportData, totalPages]
  );

  useEffect(() => {
    if (reportData && onDiagramReady && pageFrameRefs.current.length) {
      // Wait for diagram to render, then notify parent
      const timer = setTimeout(() => {
        // Report each page as { el, nodes } so the PDF capture can size itself from
        // the node coordinates (getRectOfNodes) at identity transform — the fix for
        // wide diagrams being clipped on the right. `pageLayouts[i].nodes` is the same
        // array fed to <ReactFlow nodes=…>, so no ReactFlow instance handle is needed.
        const diagramPages = pageFrameRefs.current
          .map((el, i) => (el ? { el, nodes: pageLayouts[i]?.nodes || [] } : null))
          .filter(Boolean);
        if (diagramPages.length) {
          onDiagramReady(keyId, diagramPages);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [reportData, keyId, onDiagramReady, pageLayouts]);

  if (!reportData) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const key = reportData.key || {};
  const buildingName = key.building_name || reportData.building_name || "";
  const periodLabel = formatPeriodRange(key);
  const exportDateLabel = new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  return (
    <>
    {pageLayouts.map((layout, pageIndex) => (
    <div
      key={`diagram-page-${pageIndex}`}
      ref={(el) => {
        pageFrameRefs.current[pageIndex] = el;
      }}
      className="diagram-a4-frame"
      style={{
        // Pinned width (was width:100% + maxWidth) — client reported "positions
        // no longer aligned when scrolling". Root cause: with a percentage width,
        // the browser scrollbar toggling as the report scrolls flexes the frame
        // width by ~15px, which flexes the ReactFlow container inside, which
        // triggers React Flow's internal ResizeObserver → re-projects nodes.
        // Every scrollbar toggle visibly shifted the tree. Pinning to a fixed
        // px width keeps ReactFlow's container dimensions constant so no
        // re-projection happens, and the diagram stays visually stable while
        // the user scrolls the preview. Export uses the identity-scale capture
        // path (see diagramPdfCapture.js), so this doesn't affect the PDF.
        width: `${STANDARD_A4_FRAME.widthPx}px`,
        height: `${STANDARD_A4_FRAME.minHeightPx}px`,
        margin: "0 auto 28px auto",
        backgroundColor: "#ffffff",
        padding: "0",
        boxSizing: "border-box",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        className="diagram-report-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          padding: "16px 32px 8px 32px",
          borderBottom: "2px solid rgba(0,3,63,0.1)",
          width: "100%",
          minWidth: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "28px",
              fontWeight: 700,
              color: "#00033F",
              lineHeight: 1.2,
            }}
          >
            {buildingName}
          </div>
          <div
            style={{
              fontSize: "22px",
              fontWeight: 500,
              color: "#4a4c6b",
            }}
          >
            {periodLabel}
          </div>
        </div>
        <div style={{ flexShrink: 0 }}>
          {/* M1.1b: shrunk logo from 48px to 28px to match the Phase 3
              Répartition header proportions (client feedback: "Logo and police
              are perfect on Répartition Report — model the most similar on
              Key graphs"). White-label custom logo kept proportionally larger
              at 36px so the operator's own brand reads clearly. */}
          {!whiteLabellingEnabled ? (
            <img src={repartisLogo} alt="Repartis" style={{ height: "28px", width: "auto" }} />
          ) : customLogoUrl ? (
            <img src={`${process.env.REACT_APP_STATIC_URL}${customLogoUrl}`} alt="Logo" style={{ height: "36px", width: "auto" }} />
          ) : null}
        </div>
      </div>
      <div
        className="diagram-report-body"
        style={{
          flex: 1,
          borderRadius: "16px",
          backgroundColor: "#f7f8ff",
          padding: "12px 32px",
          margin: "8px 0",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <style>{`
          .diagram-report-body .react-flow__node {
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
          }
          .diagram-report-body .react-flow__edge {
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            overflow: visible !important;
            opacity: 1 !important;
            visibility: visible !important;
            display: block !important;
          }
          .diagram-report-body .react-flow__edges {
            overflow: visible !important;
            opacity: 1 !important;
            visibility: visible !important;
            display: block !important;
          }
          /* Force all edge strokes to simple grey for consistent view + export */
          .diagram-report-body .react-flow__edge path {
            stroke: #b1b1b7 !important;
            stroke-width: 2px !important;
            fill: none !important;
            opacity: 1 !important;
            visibility: visible !important;
            display: block !important;
          }
          .diagram-report-body .react-flow__viewport {
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
          }
          .diagram-report-body .react-flow__renderer {
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
          }
          .diagram-report-body .react-flow__node:focus,
          .diagram-report-body .react-flow__node:focus-visible {
            outline: none !important;
            box-shadow: none !important;
          }
          .diagram-report-body .react-flow__node-selected {
            outline: none !important;
            box-shadow: none !important;
          }
          /* Global grey stroke override above ensures consistent export color */
          .diagram-report-body .react-flow__edge-text {
            fill: #000 !important;
            font-size: 20px !important;
            font-weight: 600 !important;
            opacity: 1 !important;
            visibility: visible !important;
            display: block !important;
          }
          .diagram-report-body .react-flow__edge-textbg {
            fill: #fff !important;
            stroke: #b1b1b7 !important;
            stroke-width: 1px !important;
            opacity: 1 !important;
            visibility: visible !important;
            display: block !important;
          }
          .diagram-report-body .react-flow__edge-label {
            opacity: 1 !important;
            visibility: visible !important;
            display: block !important;
          }
          .diagram-report-body .edge-label-html {
            opacity: 1 !important;
            visibility: visible !important;
            display: block !important;
            z-index: 1000 !important;
          }
        `}</style>
        <ReactFlowProvider>
          <div className="reactflow-capture-root" style={{ width: '100%', height: '100%', overflow: 'visible', position: 'relative' }}>
          <ReactFlow
            onInit={(instance) => {
              // Re-fit across frames so the FINAL fit runs after the A4 frame,
              // fonts and node measurements settle. fitView fits BOTH axes (incl.
              // the invisible x=1440 spacer node), so a wide 5-column graph zooms
              // down to fit horizontally and centers — no right-edge clip.
              //
              // We intentionally do NOT freeze x/zoom and then re-shift Y here:
              // that froze a STALE horizontal fit (right column + cost box ran off
              // the container, then got cropped by the PDF capture) and used the
              // cost box's negative Y (vertical drift). padding:0.06 leaves an
              // even margin on all sides so rounding can't clip the edge.
              const doFit = () =>
                instance.fitView({
                  padding: 0.06,
                  includeHiddenNodes: true,
                  maxZoom: 2.0,
                  minZoom: 0.1,
                  duration: 0,
                });
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  doFit();
                  // One more fit after layout settles (off-screen export render
                  // often settles a frame or two later than the first paint).
                  requestAnimationFrame(() => requestAnimationFrame(doFit));
                });
              });
            }}
            nodes={layout.nodes}
            edges={layout.edges}
            edgeTypes={edgeTypes}
            minZoom={0.1}
            maxZoom={2.0}
            nodesDraggable={false}
            nodesConnectable={false}
            nodesFocusable={false}
            edgesFocusable={false}
            elementsSelectable={false}
            panOnScroll={false}
            zoomOnScroll={false}
            preventScrolling={false}
            style={{ width: "100%", height: "100%" }}
            deleteKeyCode={null}
            multiSelectionKeyCode={null}
            defaultEdgeOptions={{
              animated: false,
              style: { stroke: '#b1b1b7', strokeWidth: 2 },
            }}
            fitView
            fitViewOptions={{ padding: 0.06, minZoom: 0.1, maxZoom: 2.0 }}
          >
            <Background variant="dots" gap={24} color="#e5e7eb" />
          </ReactFlow>
          </div>
        </ReactFlowProvider>
      </div>
      <div
        className="diagram-report-footer"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 32px 12px 32px",
          borderTop: "2px solid rgba(0,3,63,0.1)",
          color: "#00033F",
          fontWeight: 600,
          width: "100%",
          minWidth: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        <span>{whiteLabellingEnabled ? "Powered by core repartis" : "www.repartis.ch"}</span>
        <span>{key.key_name || ""}{totalPages > 1 ? ` — Page ${pageIndex + 1}/${totalPages}` : ""}</span>
        <span>
          Exported: {exportDateLabel}
        </span>
      </div>
    </div>
    ))}
    </>
  );
};

export default KeyDiagramView;

